// store/auth.js
import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { jwtDecode } from "jwt-decode";
import api from "@/auth/api";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n'
import publicApi from '@/auth/publicApi'


export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();

  // ===== STATE =====
  const accessToken = ref(localStorage.getItem("accessToken") || null);
  const refreshToken = ref(localStorage.getItem("refreshToken") || null);
  const loginId = ref(localStorage.getItem("loginId") || null);
  const loginName = ref(localStorage.getItem("loginName") || null);
  const isLogin = ref(localStorage.getItem("isLogin") === "true");
  const isMfaEnabled = ref(localStorage.getItem("isMfaEnabled") === "true");
  const isMfaRequired = ref(localStorage.getItem("isMfaRequired") === "true");
  const permissions = ref(JSON.parse(localStorage.getItem("permissions") || "[]"));
  const roles = ref(JSON.parse(localStorage.getItem("roles") || "[]"));
  const tenantId = ref(localStorage.getItem("tenantId") || null);

  // ===== HELPERS =====
  function setAuthData({ access, refresh, id, name, mfaEnabled, mfaRequired, tenantId: hId, rolesList, perms }) {
    accessToken.value = access;
    refreshToken.value = refresh;
    loginId.value = id;
    loginName.value = name;
    isLogin.value = true;
    isMfaEnabled.value = mfaEnabled;
    isMfaRequired.value = mfaRequired;
    tenantId.value = hId || null;
    roles.value = rolesList || [];
    permissions.value = perms || [];

    localStorage.setItem("accessToken", access);
    localStorage.setItem("refreshToken", refresh);
    localStorage.setItem("loginId", id);
    localStorage.setItem("loginName", name);
    localStorage.setItem("isLogin", "true");
    localStorage.setItem("isMfaEnabled", mfaEnabled.toString());
    localStorage.setItem("isMfaRequired", mfaRequired.toString());
    localStorage.setItem("roles", JSON.stringify(roles.value));
    localStorage.setItem("permissions", JSON.stringify(permissions.value));

    if (hId) localStorage.setItem("tenantId", hId);
    else localStorage.removeItem("tenantId");
  }

  function clearAuthData() {
    accessToken.value = null;
    refreshToken.value = null;
    loginId.value = null;
    loginName.value = null;
    isLogin.value = false;
    isMfaEnabled.value = false;
    isMfaRequired.value = false;
    permissions.value = [];
    roles.value = [];
    tenantId.value = null;

    // Remove ONLY auth-related keys
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("loginId");
    localStorage.removeItem("loginName");
    localStorage.removeItem("isLogin");
    localStorage.removeItem("isMfaEnabled");
    localStorage.removeItem("isMfaRequired");
    localStorage.removeItem("roles");
    localStorage.removeItem("permissions");
    localStorage.removeItem("tenantId");
  }

  // ===== ACTIONS =====
  async function finalizeLogin(tokens) {
    const { accessToken: access, refreshToken: refresh } = tokens;
    if (!access || !refresh) throw new Error("Missing tokens");

    const decoded = jwtDecode(access);

    // Extract tenantId
    const hId = decoded.tenantId || decoded.TenantId || null;

    // Extract roles (could be string or array)
    const rolesList = decoded.role
      ? Array.isArray(decoded.role)
        ? decoded.role
        : [decoded.role]
      : [];

    // Extract permissions (could be string, array, or repeated claims)
    let perms = [];
    if (decoded.permission) {
      if (Array.isArray(decoded.permission)) {
        perms = decoded.permission;
      } else {
        perms = [decoded.permission];
      }
    }

    setAuthData({
      access,
      refresh,
      id: decoded.nameidentifier || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
      name: decoded.name || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
      mfaEnabled: decoded.IsMfaEnabled === true || decoded.IsMfaEnabled === "True",
      mfaRequired: decoded.IsMfaRequired === true || decoded.IsMfaRequired === "True",
      tenantId: hId,
      rolesList,
      perms
    });
  }

  async function refresh() {
    if (!loginId.value || !refreshToken.value) return null;

    try {
      const res = await api.post("/Auth/refresh-token", {
        userId: loginId.value,
        refreshToken: refreshToken.value,
        accessToken: accessToken.value
      });

      const { accessToken: access, refreshToken: newRefresh } = res.data;
      const decoded = jwtDecode(access);

      const hId = decoded.tenantId || decoded.TenantId || null;

      const rolesList = decoded.role
        ? Array.isArray(decoded.role)
          ? decoded.role
          : [decoded.role]
        : [];

      let perms = [];
      if (decoded.permission) {
        perms = Array.isArray(decoded.permission)
          ? decoded.permission
          : [decoded.permission];
      }

      setAuthData({
        access,
        refresh: newRefresh,
        id: decoded.nameidentifier || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
        name: decoded.name || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        mfaEnabled: decoded.IsMfaEnabled === true || decoded.IsMfaEnabled === "True",
        mfaRequired: decoded.IsMfaRequired === true || decoded.IsMfaRequired === "True",
        tenantId: hId,
        rolesList,
        perms
      });

      return access;
    } catch (err) {
      clearAuthData();
      throw err;
    }
  }


const langStore = useLangStore()
const lang = computed(() => langStore.currentLanguage || "en")

 async function logout() {
    clearAuthData()
    router.push({ name: "PublicHome", params: { lang: lang.value } })
  }

  function hasPermission(code) {
    return permissions.value.includes(code);
  }

  return {
    accessToken,
    refreshToken,
    loginId,
    loginName,
    isLogin,
    isMfaEnabled,
    isMfaRequired,
    permissions,
    roles,
    tenantId,
    finalizeLogin,
    refresh,
    logout,
    clearAuthData,
    hasPermission
  };
});


export const useThemeStore = defineStore('theme', () => {

  const currentTheme = ref(
    localStorage.getItem('currentTheme') || 'light'
  )

  const themes = ref([])

  // ============================================================
  // LOAD THEMES
  // ============================================================

  async function loadThemes() {
    try {
      const res = await publicApi.get('/Theme')
      themes.value = res.data || []
    } catch (err) {
      console.error('Theme load failed', err)
    }
  }

  // ============================================================
  // CURRENT THEME STYLES
  // ============================================================

  const themeStyles = computed(() => {

    // ----------------------------------------------------------
    // FIRST: try database theme
    // ----------------------------------------------------------

    const dbTheme = themes.value.find(
      t => t.themeName === currentTheme.value
    )

    if (dbTheme) {
      return {
        ...dbTheme.colors,
        setOpacity: 0.9
      }
    }

    // ----------------------------------------------------------
    // SECOND: fallback themes
    // ----------------------------------------------------------

    switch (currentTheme.value) {

      case 'dark':
        return {
          background:
            'linear-gradient(135deg, #121212, #1e1e1e)',

          surface: '#1e1e1e',
          text: '#E0E0E0',

          sectionBgColor: '#1E1E2F',
          sectionTextColor: '#F0F0F0',

          setOpacity: 0.9
        }

      case 'hybrid':
        return {
          background:
            'radial-gradient(circle at center, #8e2de2, #4a00e0, #1f1c2c)',

          surface: '#2c2c54',
          text: '#BDBDBD',

          setOpacity: 0.9
        }

      default:
        return {
          background:
            'radial-gradient(circle at center, #E0E0E0, #F5F5F5, #FFFFFF)',

          surface: '#ffffff',
          text: '#002633',

          sectionBgColor: '#F5F5F5',
          sectionTextColor: '#00BCD4',

          setOpacity: 0.9
        }
    }
  })

  // ============================================================
  // SECTION THEME
  // ============================================================

  const sectionTheme = computed(() => {

    const theme = themeStyles.value || {}

    return {

      // ========================================================
      // THEME
      // ========================================================

      themeName:
        theme.themeName,

      // ========================================================
      // BASE
      // ========================================================

      background:
        theme.background,

      surface:
        theme.surface,

      text:
        theme.text,

      // ========================================================
      // BRAND / ACCENT
      // ========================================================

      primary:
        theme.primary,

      secondary:
        theme.secondary,

      // ========================================================
      // NAVIGATION
      // ========================================================

      systemBarBg:
        theme.systemBarBg,

      systemBarColor:
        theme.systemBarColor,

      systemBarBtnBg:
        theme.systemBarBtnBg,

      systemBarBtnColor:
        theme.systemBarBtnColor,

      appBarBg:
        theme.appBarBg,

      appBarColor:
        theme.appBarColor,

      appBarBtnBg:
        theme.appBarBtnBg,

      // FIXED
      appBarBtnColor:
        theme.appBarBtnColor,

      // ========================================================
      // SECTIONS
      // ========================================================

      sectionBgColor:
        theme.sectionBgColor,

      sectionTextColor:
        theme.sectionTextColor,

      sectionAlternateBgColor:
        theme.sectionAlternateBgColor,

      sectionAlternateTextColor:
        theme.sectionAlternateTextColor,

      // ========================================================
      // CARDS
      // ========================================================

      cardTitleBgColor:
        theme.cardTitleBgColor,

      cardTitleTextColor:
        theme.cardTitleTextColor,

      cardBodyBg:
        theme.cardBodyBg,

      cardBodyColor:
        theme.cardBodyColor,

      cardAlternateBgColor:
        theme.cardAlternateBgColor,

      // ========================================================
      // BUTTONS
      // ========================================================

      btnBgColor:
        theme.btnBgColor,

      btnColor:
        theme.btnColor,

      btnAlternateBgColor:
        theme.btnAlternateBgColor,

      btnAlternateColor:
        theme.btnAlternateColor,

      // ========================================================
      // TABLES
      // ========================================================

      tableCaptionBgColor:
        theme.tableCaptionBgColor,

      tablecaptionTextColor:
        theme.tablecaptionTextColor,

      tableRowBgColor:
        theme.tableRowBgColor,

      tableRowTextColor:
        theme.tableRowTextColor,

      tableRowAltBgColor:
        theme.tableRowAltBgColor,

      tableRowAltTextColor:
        theme.tableRowAltTextColor,

      // ========================================================
      // FOOTER
      // ========================================================

      footerBarBg:
        theme.footerBarBg,

      footerColor:
        theme.footerColor,

      footerBtnBg:
        theme.footerBtnBg,

      footerBtnColor:
        theme.footerBtnColor,

      footerLastDivColor:
        theme.footerLastDivColor,

      footerLastDivBtnBg:
        theme.footerLastDivBtnBg,

      appFooterLastDivBtnColor:
        theme.appFooterLastDivBtnColor,

      // ========================================================
      // GENERAL
      // ========================================================
      topBannerGradient:
        theme.topBannerGradient,
      buttonsGradient:
        theme.buttonsGradient,
      sectionGradient:
        theme.sectionGradient,
      sectionAltGradient:
        theme.sectionAltGradient,

      setOpacity:
        theme.setOpacity
    }
  })

  // ============================================================
  // RESOLVE NORMAL / ALTERNATE SECTION
  // ============================================================

  function getSectionTheme(index = 0) {

    const isAlternate =
      index % 2 !== 0

    const theme =
      sectionTheme.value

    return {
      ...theme,

      resolvedSectionBgColor:
        isAlternate
          ? theme.sectionAlternateBgColor
          : theme.sectionBgColor,

      resolvedSectionTextColor:
        isAlternate
          ? theme.sectionAlternateTextColor
          : theme.sectionTextColor
    }
  }

  // ============================================================
  // SET THEME
  // ============================================================

  function setTheme(theme) {
    currentTheme.value = theme
    localStorage.setItem(
      'currentTheme',
      theme
    )
  }

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  loadThemes()

  // ============================================================
  // EXPORT
  // ============================================================

  return {
    currentTheme,
    themeStyles,
    sectionTheme,
    getSectionTheme,
    setTheme,
    themes,
    loadThemes
  }
})



export const useLangStore = defineStore('lang', () => {
  const { locale } = useI18n()

  // load saved language or default to 'en'
  const currentLang = ref(localStorage.getItem('locale') || 'en')

  // keep vue-i18n locale in sync
  locale.value = currentLang.value

  // available languages
  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇨🇦' }
  ]

  // get the current flag
  const currentFlag = computed(() => {
    const lang = languages.find(l => l.code === currentLang.value)
    return lang ? lang.flag : '🇺🇸'
  })

  // change language
  function setLanguage(code) {
    currentLang.value = code
    locale.value = code   // update vue-i18n
    localStorage.setItem('locale', code)
  }

  // auto-sync if currentLang changes
  watch(currentLang, (newVal) => {
    locale.value = newVal
    localStorage.setItem('locale', newVal)
  })

  return { currentLang, currentFlag, languages, setLanguage }
})
